
import ApiHelper from '../../ApiHelper';
import API_ENDPOINTS from '../../endpoints';
import axios from 'axios';

// --- Helpers ---
function extractErrorMessage(err) {
  if (!err) return 'Unknown error';
  if (err.response && err.response.data) {
    const d = err.response.data;
    if (typeof d === 'string') return d;
    if (d.message) return d.message;
    if (d.errors) return JSON.stringify(d.errors);
    return JSON.stringify(d);
  }
  if (err.message) return err.message;
  return String(err);
}

function cleanContentPayload(raw = {}) {
  const {
    title,
    type,
    content_url,
    duration_minutes,
    order_index,
    is_mandatory,
    content_data,
    ...other
  } = raw || {};

  const payload = {
    ...(typeof title === 'string' && title.trim() ? { title: title.trim() } : {}),
    ...(typeof type === 'string' && type.trim() ? { type: type.trim() } : {}),
    ...(typeof content_url === 'string' && content_url.trim() 
      ? { content_url: content_url.trim() } 
      : {}),
    
    // Handle numeric fields
    ...(duration_minutes !== undefined && duration_minutes !== null && duration_minutes !== ''
      ? (() => {
          const num = Number(duration_minutes);
          return Number.isFinite(num) ? { duration_minutes: num } : {};
        })()
      : {}),
    
    ...(order_index !== undefined && order_index !== null && order_index !== ''
      ? (() => {
          const num = Number(order_index);
          return Number.isFinite(num) ? { order_index: num } : {};
        })()
      : {}),
    
    // Handle boolean
    ...(typeof is_mandatory === 'boolean' ? { is_mandatory } : {}),
    
    // Handle JSON object
    ...(content_data && typeof content_data === 'object' 
      ? { content_data } 
      : {}),
    
    // Include other primitive non-empty keys defensively
    ...Object.fromEntries(
      Object.entries(other).filter(([k, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      })
    )
  };

  return payload;
}

function cleanReorderPayload(newOrder = []) {
  if (!Array.isArray(newOrder)) return [];
  
  return newOrder
    .filter(item => item && item.id)
    .map(item => ({
      id: item.id,
      order_index: Number(item.order_index) || 1
    }));
}

// --- Course Content Helper Class ---
class CourseContentHelper {
  /**
   * Get all contents for a course
   * @param {string} courseId - Course UUID
   * @param {object} params - Query parameters (type, is_mandatory, etc.)
   */
  async getCourseContents(courseId, params = {}) {
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS(courseId), 
        params
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch course contents: ${extractErrorMessage(error)}`);
    }
  }

  /**
   * Get single content by ID
   * @param {string} contentId - Content UUID
   */
  async getCourseContent(contentId) {
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENT_BY_ID(contentId)
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch course content: ${extractErrorMessage(error)}`);
    }
  }

  /**
   * Create new course content
   * @param {string} courseId - Course UUID
   * @param {object} contentData - Content data
   */
  async createCourseContent(courseId, contentData) {
    const payload = cleanContentPayload(contentData);

    // DEBUG (remove or gate behind env flag if needed)
    // console.log('createCourseContent payload:', JSON.stringify(payload, null, 2));

    try {
      const response = await ApiHelper.post(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS(courseId),
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response;
    } catch (err) {
      // Fallback to axios if ApiHelper fails
      try {
        const url = API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS(courseId);
        const axiosResp = await axios.post(url, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
        return axiosResp.data;
      } catch (axiosErr) {
        throw new Error(`Failed to create course content: ${extractErrorMessage(axiosErr)}`);
      }
    }
  }

  /**
   * Update course content
   * @param {string} contentId - Content UUID
   * @param {object} updateData - Update data
   */
  async updateCourseContent(contentId, updateData) {
    const payload = cleanContentPayload(updateData);

    try {
      const response = await ApiHelper.post(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENT_BY_ID(contentId),
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response;
    } catch (err) {
      // Fallback to axios if ApiHelper fails
      try {
        const url = API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENT_BY_ID(contentId);
        const axiosResp = await axios.post(url, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
        return axiosResp.data;
      } catch (axiosErr) {
        throw new Error(`Failed to update course content: ${extractErrorMessage(axiosErr)}`);
      }
    }
  }

  /**
   * Delete course content
   * @param {string} contentId - Content UUID
   */
  async deleteCourseContent(contentId) {
    try {
      const response = await ApiHelper.delete(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENT_BY_ID(contentId)
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to delete course content: ${extractErrorMessage(error)}`);
    }
  }

  /**
   * Reorder course contents
   * @param {string} courseId - Course UUID
   * @param {array} newOrder - Array of {id, order_index} objects
   */
  async reorderCourseContents(courseId, newOrder) {
    const cleanedOrder = cleanReorderPayload(newOrder);

    if (cleanedOrder.length === 0) {
      throw new Error('Invalid reorder data: newOrder must be a non-empty array');
    }

    try {
      const response = await ApiHelper.post(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS_REORDER(courseId),
        { newOrder: cleanedOrder },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response;
    } catch (err) {
      // Fallback to axios
      try {
        const url = API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS_REORDER(courseId);
        const axiosResp = await axios.post(url, { newOrder: cleanedOrder }, {
          headers: { 'Content-Type': 'application/json' }
        });
        return axiosResp.data;
      } catch (axiosErr) {
        throw new Error(`Failed to reorder course contents: ${extractErrorMessage(axiosErr)}`);
      }
    }
  }

  /**
   * Get course content statistics
   * @param {string} courseId - Course UUID
   */
  async getCourseContentStatistics(courseId) {
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.ADMIN.COURSES.COURSE_CONTENTS_STATISTICS(courseId)
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch content statistics: ${extractErrorMessage(error)}`);
    }
  }
}

export default new CourseContentHelper();
























